import { toast } from 'react-toastify';

let ePosDevice = null;
let printer = null;

// Function to automatically load ePOS-Print library and handle certificate
export const initializePrinterEnvironment = async (ipAddress) => {
  try {
    // Check if ePOS library is already loaded
    if (window.epson && window.epson.ePOSDevice) {
      return true;
    }

    // Load the ePOS-Print JavaScript library from the printer
    await loadEPOSLibrary(ipAddress);

    // Optional: Attempt to handle certificate trust automatically
    await handleCertificateTrust(ipAddress);

    return true;
  } catch (error) {
    console.error('❌ Failed to initialize printer environment:', error);
    toast.error(
      'Failed to initialize printer environment. Please try connecting manually.'
    );
    throw error;
  }
};

// Function to detect available ePOS-Print library version
const detectEPOSVersion = async (ipAddress) => {
  const commonVersions = [
    'epos-print-5.2.0.js',
    'epos-print-5.1.0.js',
    'epos-print-5.0.0.js',
    'epos-print-4.7.0.js',
    'epos-print-4.6.0.js',
    'epos-print-4.5.0.js',
    'epos-print-4.4.0.js',
    'epos-print-4.3.0.js',
    'epos-print-4.2.0.js',
    'epos-print-4.1.0.js',
    'epos-print-4.0.0.js',
  ];

  for (const version of commonVersions) {
    try {
      await fetch(`https://${ipAddress}/js/${version}`, {
        method: 'HEAD',
        mode: 'no-cors', // Handle CORS issues
      });
      return version;
    } catch (error) {
      // Continue to next version
      continue;
    }
  }

  // If no specific version found, try generic names
  const genericNames = ['epos-print.js', 'epos-print-5.js', 'epos-print-4.js'];

  for (const genericName of genericNames) {
    try {
      await fetch(`https://${ipAddress}/js/${genericName}`, {
        method: 'HEAD',
        mode: 'no-cors',
      });
      return genericName;
    } catch (error) {
      continue;
    }
  }

  // Fallback to most common version
  console.warn('⚠️ Could not detect ePOS version, using fallback');
  return 'epos-print-4.5.0.js';
};

// Load ePOS-Print library dynamically with version detection
const loadEPOSLibrary = async (ipAddress) => {
  return new Promise(async (resolve, reject) => {
    // Check if already loaded
    if (window.epson && window.epson.ePOSDevice) {
      resolve();
      return;
    }

    try {
      // Try to detect the correct version
      const detectedVersion = await detectEPOSVersion(ipAddress);

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = `https://${ipAddress}/js/${detectedVersion}`;

      script.onload = () => {
        resolve();
      };

      script.onerror = async () => {
        console.warn(
          `⚠️ Failed to load ${detectedVersion}, trying fallback methods...`
        );

        // Try alternative loading methods
        const fallbackVersions = [
          'epos-print-4.5.0.js',
          'epos-print-4.6.0.js',
          'epos-print.js',
        ];

        let loaded = false;
        for (const fallbackVersion of fallbackVersions) {
          if (loaded) break;

          try {
            // eslint-disable-next-line no-loop-func
            await new Promise((fallbackResolve, fallbackReject) => {
              const fallbackScript = document.createElement('script');
              fallbackScript.type = 'text/javascript';
              fallbackScript.src = `https://${ipAddress}/js/${fallbackVersion}`;

              fallbackScript.onload = () => {
                loaded = true;
                fallbackResolve();
              };

              fallbackScript.onerror = () => {
                fallbackReject(new Error(`Failed to load ${fallbackVersion}`));
              };

              document.head.appendChild(fallbackScript);
            });

            if (loaded) {
              resolve();
              return;
            }
          } catch (fallbackError) {
            console.warn(`❌ Fallback failed for ${fallbackVersion}`);
            continue;
          }
        }

        // If all fallbacks failed
        if (!loaded) {
          console.error(
            '❌ Failed to load ePOS library with all attempted versions'
          );
          toast.error(
            `Failed to load ePOS library from printer. Please navigate to https://${ipAddress} first to check available versions.`
          );
          reject(new Error('Failed to load ePOS-Print library'));
        }
      };

      document.head.appendChild(script);
    } catch (error) {
      console.error('❌ Error during version detection:', error);
      toast.error(
        `Failed to load ePOS library from printer. Please navigate to https://${ipAddress} first.`
      );
      reject(new Error('Failed to load ePOS-Print library'));
    }
  });
};

// Handle certificate trust by creating a hidden iframe
const handleCertificateTrust = (ipAddress) => {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = `https://${ipAddress}`;

    const cleanup = () => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    };

    iframe.onload = () => {
      setTimeout(() => {
        cleanup();
        resolve();
      }, 1000);
    };

    iframe.onerror = () => {
      console.warn('⚠️ Certificate trust may need manual handling');
      cleanup();
      resolve(); // Don't reject, as this might still work
    };

    document.body.appendChild(iframe);

    // Timeout after 5 seconds
    setTimeout(() => {
      cleanup();
      resolve();
    }, 5000);
  });
};

// Enhanced connection function with auto-initialization
export const connectPrinter = async (
  ipAddress,
  setConnectionStatus,
  setIsVisible
) => {
  try {
    // Check if already connected
    if (
      printer &&
      ePosDevice &&
      ePosDevice.isConnected &&
      ePosDevice.isConnected()
    ) {
      setConnectionStatus('Already connected');
      return true;
    }

    if (!ipAddress) {
      throw new Error('No IP address provided');
    }

    setConnectionStatus('Initializing printer environment...');

    // Try to initialize the printer environment
    try {
      await initializePrinterEnvironment(ipAddress);
    } catch (initError) {
      console.warn(
        '⚠️ Auto-initialization failed, trying direct connection...'
      );
      // Continue anyway, might still work if manually navigated
    }

    // Clean up any existing connections
    if (ePosDevice) {
      try {
        ePosDevice.disconnect();
      } catch (error) {
        console.warn('Error disconnecting existing device:', error);
      }
    }

    printer = null;
    ePosDevice = null;

    // Check if ePOS library is available
    if (!window.epson || !window.epson.ePOSDevice) {
      const errorMsg = `ePOS-Print library not available. Please navigate to https://${ipAddress} first to load the library and accept the certificate.`;
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }

    return new Promise((resolve, reject) => {
      setConnectionStatus('Connecting to printer...');
      ePosDevice = new window.epson.ePOSDevice();

      const connectionTimeout = setTimeout(() => {
        setConnectionStatus('Connection timeout');
        setIsVisible(false);
        toast.error(
          'Connection timeout. Please check printer network settings and try again.'
        );
        if (ePosDevice) {
          try {
            ePosDevice.disconnect();
          } catch (error) {
            console.warn('Error during timeout cleanup:', error);
          }
        }
        ePosDevice = null;
        reject(new Error('Connection timeout'));
      }, 15000); // Increased timeout for initialization

      ePosDevice.connect(ipAddress, 8043, (data) => {
        clearTimeout(connectionTimeout);

        if (data === 'SSL_CONNECT_OK') {
          ePosDevice.createDevice(
            'local_printer',
            ePosDevice.DEVICE_TYPE_PRINTER,
            { crypto: true, buffer: false },
            (devobj, retcode) => {
              if (retcode === 'OK') {
                printer = devobj;
                setConnectionStatus('Connected');
                setIsVisible(false);

                if (printer.onerror) {
                  printer.onerror = (error) => {
                    console.error('Printer error:', error);
                    setConnectionStatus('Printer error occurred');
                    toast.error('Printer error occurred during operation');
                  };
                }

                resolve(true);
              } else {
                console.error('❌ Device creation failed:', retcode);
                setConnectionStatus(`Connection failed: ${retcode}`);
                setIsVisible(false);

                try {
                  ePosDevice.disconnect();
                } catch (error) {
                  console.error('Error during cleanup:', error);
                }
                ePosDevice = null;

                reject(new Error(`Device creation failed: ${retcode}`));
              }
            }
          );
        } else {
          console.error('❌ SSL connection failed:', data);
          let errorMessage = `Connection failed: ${data}`;
          let toastMessage = 'SSL connection failed.';

          if (data.includes('SSL') || data.includes('certificate')) {
            errorMessage +=
              '\n\nPlease navigate to https://' +
              ipAddress +
              ' in your browser first to accept the SSL certificate.';
            toastMessage = `SSL certificate issue. Please visit https://${ipAddress} to accept the certificate first.`;
          } else if (data.includes('ENOTFOUND') || data.includes('timeout')) {
            toastMessage = `Cannot reach printer at ${ipAddress}. Please check network connection.`;
          } else {
            toastMessage = `Connection failed: ${data}`;
          }

          setConnectionStatus(errorMessage);
          setIsVisible(false);
          toast.error(toastMessage);

          if (ePosDevice) {
            try {
              ePosDevice.disconnect();
            } catch (error) {
              console.warn('Error during cleanup:', error);
            }
          }
          ePosDevice = null;

          reject(new Error(errorMessage));
        }
      });
    });
  } catch (error) {
    console.error('❌ Connection error:', error);
    setConnectionStatus(error.message);
    setIsVisible(false);

    // Only show toast if it's not already handled by inner functions
    if (!error.message.includes('ePOS-Print library not available')) {
      toast.error(`Connection error: ${error.message}`);
    }

    throw error;
  }
};

// Function to open printer web interface for manual setup
export const openPrinterWebInterface = (ipAddress) => {
  const url = `https://${ipAddress}`;
  window.open(url, '_blank', 'width=800,height=600');

  return new Promise((resolve) => {
    // Give user time to accept certificate and load library
    const checkInterval = setInterval(() => {
      if (window.epson && window.epson.ePOSDevice) {
        clearInterval(checkInterval);
        resolve(true);
      }
    }, 1000);

    // Timeout after 30 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
      resolve(false);
    }, 30000);
  });
};

// Enhanced connection with user guidance
export const connectPrinterWithSetup = async (
  ipAddress,
  setConnectionStatus,
  setIsVisible
) => {
  try {
    // Try direct connection first
    return await connectPrinter(ipAddress, setConnectionStatus, setIsVisible);
  } catch (error) {
    if (
      error.message.includes('ePOS-Print library not available') ||
      error.message.includes('SSL') ||
      error.message.includes('certificate')
    ) {
      setConnectionStatus('Opening printer setup...');

      // Guide user through manual setup
      const setupMessage = `To connect to the printer, please:
1. Click OK to open the printer's web interface
2. Accept the SSL certificate warning
3. Wait for the page to load completely
4. Close the printer web page
5. Try connecting again`;

      toast.error(
        'Printer setup required. Please complete the manual setup process.'
      );

      // eslint-disable-next-line no-restricted-globals
      if (confirm(setupMessage)) {
        await openPrinterWebInterface(ipAddress);
        setConnectionStatus(
          'Please try connecting again after completing setup'
        );
        toast.info(
          'Setup window opened. Complete the setup and try connecting again.'
        );
        return false;
      } else {
        toast.info(
          'Printer setup cancelled. Manual setup is required to connect.'
        );
      }
    }
    throw error;
  }
};

export const isConnectedCheck = () => {
  // Pure check without side effects
  return (
    printer !== null &&
    ePosDevice !== null &&
    ePosDevice.isConnected &&
    ePosDevice.isConnected()
  );
};

// Modified getPrinter to be less aggressive with cleanup
export const getPrinter = () => {
  if (printer) {
    // Only check connection status without immediate cleanup
    if (ePosDevice && ePosDevice.isConnected && ePosDevice.isConnected()) {
      return printer;
    } else {
      console.warn('⚠️ Printer device is no longer connected');
      // Don't immediately clean up - let explicit disconnect handle this
      return null;
    }
  }

  return null;
};

// Separate cleanup function to call explicitly when needed
export const cleanupDisconnectedPrinter = () => {
  if (printer && ePosDevice) {
    if (!ePosDevice.isConnected || !ePosDevice.isConnected()) {
      printer = null;
      ePosDevice = null;
      return true; // Cleanup performed
    }
  }
  return false; // No cleanup needed
};

export const disconnectPrinter = () => {
  try {
    if (printer) {
      if (printer.onerror) {
        printer.onerror = null;
      }
      printer = null;
    }

    if (ePosDevice) {
      if (ePosDevice.isConnected && ePosDevice.isConnected()) {
        ePosDevice.disconnect();
      }
      ePosDevice = null;
    }

    toast.success('Printer disconnected successfully');
  } catch (error) {
    console.error('❌ Error during disconnect:', error);
    toast.error('Error occurred while disconnecting printer');
    printer = null;
    ePosDevice = null;
  }
};

export const getConnectionStatus = () => {
  if (!window.epson || !window.epson.ePOSDevice)
    return 'ePOS library not loaded';
  if (!ePosDevice) return 'Not initialized';
  if (!ePosDevice.isConnected || !ePosDevice.isConnected())
    return 'Disconnected';
  if (!printer) return 'Device connected but printer not ready';
  return 'Fully connected';
};

// Utility function to manually check what ePOS versions are available
export const checkAvailableVersions = async (ipAddress) => {
  const versionsToCheck = [
    'epos-print-5.2.0.js',
    'epos-print-5.1.0.js',
    'epos-print-5.0.0.js',
    'epos-print-4.7.0.js',
    'epos-print-4.6.0.js',
    'epos-print-4.5.0.js',
    'epos-print-4.4.0.js',
    'epos-print-4.3.0.js',
    'epos-print-4.2.0.js',
    'epos-print-4.1.0.js',
    'epos-print-4.0.0.js',
    'epos-print.js',
  ];

  const availableVersions = [];

  for (const version of versionsToCheck) {
    try {
      // Use a simple image load trick to test if file exists
      await new Promise((resolve, reject) => {
        const img = new Image();
        const timeout = setTimeout(() => {
          reject(new Error('timeout'));
        }, 2000);

        img.onload = () => {
          clearTimeout(timeout);
          resolve();
        };

        img.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('not found'));
        };

        img.src = `https://${ipAddress}/js/${version}`;
      });

      availableVersions.push(version);
    } catch (error) {
      // File doesn't exist or can't be accessed
      console.error(`❌ Not available: ${error}`);
    }
  }

  if (availableVersions.length > 0) {
    toast.success(`Found ${availableVersions.length} available ePOS versions`);
  } else {
    console.error('❌ No ePOS versions could be detected');
    toast.info(
      'Could not detect available versions. Try navigating to the printer web interface first.'
    );
  }

  return availableVersions;
};

// Function to get printer information including software version
export const getPrinterInfo = async (ipAddress) => {
  try {
    // This requires navigating to the printer first due to CORS
    const response = await fetch(
      `https://${ipAddress}/cgi-bin/epos/service.cgi?devid=local_printer&timeout=10000`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'text/xml; charset=utf-8',
          SOAPAction:
            'http://www.epson-pos.com/schemas/pos/eposprint/RequestResponse',
        },
        body: `<?xml version="1.0" encoding="utf-8"?>
        <soap:Envelope xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">
          <soap:Body>
            <epos-print xmlns="http://www.epson-pos.com/schemas/pos/eposprint">
              <parameter>
                <devid>local_printer</devid>
                <timeout>10000</timeout>
              </parameter>
              <epos-print>
                <status/>
              </epos-print>
            </epos-print>
          </soap:Body>
        </soap:Envelope>`,
      }
    );

    const xmlText = await response.text();
    return xmlText;
  } catch (error) {
    console.error('Failed to get printer info:', error);
    toast.info(
      'Navigate to printer web interface first to enable printer info retrieval'
    );
    return null;
  }
};
